import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { TechDataAPI, extractError } from "../lib/api";

const CATEGORIES = [
    { v: "", label: "All categories" },
    { v: "maintenance_schedule", label: "Maintenance schedule" },
    { v: "torque_spec", label: "Torque specs" },
    { v: "fluid_spec", label: "Fluid specs" },
    { v: "belt_chain", label: "Belt / Chain" },
    { v: "wiring_circuit", label: "Wiring" },
    { v: "repair_procedure", label: "Repair procedure" },
    { v: "service_bulletin", label: "Service bulletin" },
    { v: "recall_notice", label: "Recall" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.v, c.label]));

export default function TechDataPage() {
    const { permissions } = useAuth();
    const nav = useNavigate();
    const canWrite = (permissions || []).includes("techdata:write");

    const [make, setMake] = useState("");
    const [model, setModel] = useState("");
    const [year, setYear] = useState("");
    const [category, setCategory] = useState("");
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        const params = { page_size: 100 };
        if (make) params.make = make;
        if (model) params.model = model;
        if (year) params.year = Number(year);
        if (category) params.category = category;
        if (q) params.q = q;
        TechDataAPI.list(params)
            .then((d) => { setItems(d.items || []); setErr(null); })
            .catch((e) => { setErr(extractError(e)); setItems([]); })
            .finally(() => setLoading(false));
    }, [make, model, year, category, q]);

    useEffect(() => { load(); }, [load]);

    const grouped = items.reduce((acc, it) => {
        const key = it.category;
        (acc[key] = acc[key] || []).push(it);
        return acc;
    }, {});

    return (
        <div data-testid="tech-data-page" className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Service Specs</h1>
                {canWrite && (
                    <Button asChild data-testid="tech-data-new-btn">
                        <Link to="/tech-data/new">New entry</Link>
                    </Button>
                )}
            </div>

            <div data-testid="tech-data-search" className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                <Input data-testid="tech-data-search-make" placeholder="Make (e.g. Maruti Suzuki)" value={make} onChange={(e) => setMake(e.target.value)} />
                <Input data-testid="tech-data-search-model" placeholder="Model (e.g. Swift)" value={model} onChange={(e) => setModel(e.target.value)} />
                <Input data-testid="tech-data-search-year" type="number" placeholder="Year" value={year} onChange={(e) => setYear(e.target.value)} />
                <Select value={category || "_all"} onValueChange={(v) => setCategory(v === "_all" ? "" : v)}>
                    <SelectTrigger data-testid="tech-data-search-category"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All categories</SelectItem>
                        {CATEGORIES.filter((c) => c.v).map((c) => (
                            <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Input data-testid="tech-data-search-q" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>

            {loading && <div className="text-sm opacity-60">Loading…</div>}
            {err && <div className="text-sm text-rose-600">{err}</div>}

            {Object.keys(grouped).sort().map((cat) => (
                <section key={cat} data-testid={`tech-data-category-${cat}`}>
                    <h2 className="text-xs uppercase tracking-widest opacity-70 mb-2">
                        {CATEGORY_LABEL[cat] || cat}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {grouped[cat].map((e) => (
                            <button key={e.id} type="button"
                                       onClick={() => nav(`/tech-data/${e.id}`)}
                                       data-testid={`tech-data-card-${e.id}`}
                                       className="text-left rounded-xl border bg-card p-3 hover:bg-muted/40 transition-colors">
                                <div className="text-xs opacity-60 tracking-wider uppercase mb-1">
                                    {[e.make, e.model, e.year_range_start && (e.year_range_end ? `${e.year_range_start}-${e.year_range_end}` : `${e.year_range_start}+`)].filter(Boolean).join(" · ")}
                                    {e.is_system ? "" : " · custom"}
                                </div>
                                <div className="font-medium text-sm">{e.title}</div>
                            </button>
                        ))}
                    </div>
                </section>
            ))}

            {!loading && items.length === 0 && (
                <div data-testid="tech-data-empty" className="text-sm opacity-60 rounded-xl border border-dashed p-8 text-center">
                    No entries match the current filters.
                </div>
            )}
        </div>
    );
}
